import {
  SpaceBetween,
  Checkbox,
  Multiselect,
  Box,
  Slider,
  FormField,
  Modal
} from '@cloudscape-design/components';
import { KBOptions } from '../../../lib/models/interfaces';
import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { OptionDefinition } from '@cloudscape-design/components/internal/components/option/interfaces';
import { ProjectContext } from '../../../context/projectContext';
import { DocumentContext } from '../../../context/documentContext';
import { SquareX } from 'lucide-react';
import { ScrollDownArrow } from '../shared/scrollDownArrow';
import { useLocation } from 'react-router-dom';

interface DocFilterProps {
  KBChecked: boolean;
  setKBChecked: (arg: boolean) => void;
  kbFilters: OptionDefinition[];
  setKBFilters: (arg: OptionDefinition[]) => void;
  prevChecked: boolean;
  setPrevChecked: (arg: boolean) => void;
  selectedDocuments: OptionDefinition[];
  setSelectedDocuments: (arg: OptionDefinition[]) => void;
  similarityThreshold: number;
  setSimilarThreshold: (arg: number) => void;
  filterIsOpen?: boolean;
  type: string;
}

export interface DocumentFilterHandle {
  reset: () => void;
}

export const DocumentFilter = forwardRef<DocumentFilterHandle, DocFilterProps>(
  (
    {
      KBChecked,
      setKBChecked,
      kbFilters,
      setKBFilters,
      prevChecked,
      setPrevChecked,
      selectedDocuments,
      setSelectedDocuments,
      similarityThreshold,
      setSimilarThreshold,
      filterIsOpen,
      type
    },
    ref
  ) => {
    // context

    const { mainProjects, allFolders } = useContext(ProjectContext);
    const { allDocuments } = useContext(DocumentContext);
    const filterRef = useRef<HTMLDivElement>(null);
    const location = useLocation();
    // state
    const [projectSelectList, setProjectSelectList] = useState<
      OptionDefinition[]
    >([]);
    const [selectedProject, setSelecetedProject] = useState<OptionDefinition[]>(
      []
    );
    const [folderSelectList, setFolderSelectList] = useState<
      OptionDefinition[]
    >([]);
    const [selectedFolders, setSelectedFolders] = useState<OptionDefinition[]>(
      []
    );
    const [documentsSelectList, setDocumentSelectList] = useState<
      OptionDefinition[]
    >([]);
    const [showModal, setShowModal] = useState<boolean>(false);

    const setProjectsOptions = () => {
      const optionsArray = mainProjects
        .filter((ele) => ele.projectType === 'workspace')
        .filter((ele) => {
          if (
            allDocuments.filter((doc) => ele._id === doc?.project).length > 0
          ) {
            return true;
          } else {
            return false;
          }
        })
        .map((ele) => {
          return {
            label: ele.projectName,
            value: ele._id
          };
        });
      setProjectSelectList(optionsArray);
    };
    useEffect(() => {
      if (mainProjects.length > 0) {
        setProjectsOptions();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mainProjects]);

    useEffect(() => {
      if (selectedProject.length > 0) {
        const projectIds = selectedProject.map((ele) => ele.value);
        const foldersArray = allFolders.filter((ele) =>
          projectIds.includes(ele.project)
        );
        const folderOptionList = foldersArray
          .map((ele) => {
            const projectName = mainProjects.filter(
              (p) => p._id === ele.project
            )[0].projectName;
            return {
              label: `(${projectName}) - ${ele.folderName}`,
              value: ele._id
            };
          })
          .filter((f) => {
            if (allDocuments.filter((d) => d.folder === f.value).length > 0) {
              return true;
            } else {
              return false;
            }
          });
        setFolderSelectList(folderOptionList);
      } else {
        setFolderSelectList([]);
        setDocumentSelectList([]);
        setSelectedFolders([]);
        setSelectedDocuments([]);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProject]);

    useEffect(() => {
      if (selectedFolders.length === 0) {
        setDocumentSelectList([]);
        setSelectedDocuments([]);
      }
      if (allDocuments.length === 0) {
        setShowModal(true);
        return;
      }
      const folderIds = selectedFolders.map((ele) => ele.value);
      const filteredDocs = allDocuments.filter((ele) =>
        folderIds.includes(ele.folder)
      );
      const docOptions = filteredDocs.map((ele) => {
        return { label: ele.documentFileName, value: ele._id };
      });
      setDocumentSelectList(docOptions);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allDocuments, selectedFolders]);

    const handleClear = () => {
      setKBChecked(false);
      setPrevChecked(false);
      setKBFilters([]);
      setSelecetedProject([]);
      setSelectedFolders([]);
      setSelectedDocuments([]);
      setSimilarThreshold(0.7);
    };

    useImperativeHandle(ref, () => ({
      reset: handleClear
    }));
    useEffect(() => {
      if (filterIsOpen && allDocuments.length === 0) {
        setShowModal(true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterIsOpen]);

    const showPrevQuestions = location.pathname === '/app/rfp-upload';

    const i18nStrings = { selectAllText: 'Select All' };
    return (
      <div
        className="w-full h-[46vh] bg-blue-50 overflow-y-scroll border border-blue-200 rounded-lg p-4 shadow-sm text-sm text-gray-800 flex flex-col space-y-6 relative"
        style={{ scrollbarWidth: 'none' }}
        ref={filterRef}
      >
        <SpaceBetween direction="vertical" size={'xs'}>
          <>
            <div className="relative">
              <Box>
                Select the Source Documents You Want the AI to Use to Answer
                your Questions
              </Box>
              <div className="absolute right-0 top-0">
                <SquareX size={18} onClick={() => handleClear()} />
              </div>
            </div>
            <SpaceBetween size={'xs'} direction="vertical">
              {showPrevQuestions && (
                <>
                  <Checkbox
                    checked={prevChecked}
                    onChange={({ detail }) => setPrevChecked(detail.checked)}
                  >
                    Previous Questionnaires
                  </Checkbox>
                  {prevChecked && (
                    <FormField label="Select Similarity Threshold">
                      <Slider
                        value={Math.round(similarityThreshold * 100)}
                        step={1}
                        min={70}
                        max={99}
                        onChange={({ detail }) =>
                          setSimilarThreshold(detail.value / 100)
                        }
                        ariaLabel="Similarity Threshold"
                        valueFormatter={(e) => `${e}%`}
                      />
                    </FormField>
                  )}
                </>
              )}
              <div className="border border-slate-800 rounded-lg p-4">
                <Checkbox
                  checked={KBChecked}
                  onChange={({ detail }) => setKBChecked(detail.checked)}
                >
                  Global Library
                </Checkbox>
                {KBChecked && (
                  <Multiselect
                    onChange={({ detail }) =>
                      setKBFilters([...detail.selectedOptions])
                    }
                    // hideTokens
                    selectedOptions={kbFilters}
                    options={KBOptions}
                    placeholder={
                      kbFilters.length > 0
                        ? 'Use Selected Filters'
                        : 'Select Filters'
                    }
                    tokenLimit={5}
                    expandToViewport
                  />
                )}
              </div>
            </SpaceBetween>

            {/* <Box>AND</Box> */}
            <SpaceBetween size="xs" direction="vertical">
              <div className="border border-slate-800 rounded-lg p-4">
                <Multiselect
                  selectedOptions={selectedProject}
                  placeholder="Select Document Project"
                  options={projectSelectList}
                  onChange={({ detail }) =>
                    setSelecetedProject([...detail.selectedOptions])
                  }
                  tokenLimit={3}
                  expandToViewport
                  enableSelectAll
                  i18nStrings={i18nStrings}
                />
              </div>

              {folderSelectList.length > 0 && (
                <div className="border border-slate-800 rounded-lg p-4">
                  <Multiselect
                    placeholder="Select Folders"
                    selectedOptions={selectedFolders}
                    options={folderSelectList}
                    onChange={({ detail }) => {
                      console.log('dtail', detail);
                      setSelectedFolders([...detail.selectedOptions]);
                    }}
                    tokenLimit={3}
                    expandToViewport
                    enableSelectAll
                    i18nStrings={i18nStrings}
                  />
                </div>
              )}

              {documentsSelectList.length > 0 && (
                <div className="border border-slate-800 rounded-lg p-4">
                  <Multiselect
                    placeholder="Select Documents"
                    selectedOptions={selectedDocuments}
                    options={documentsSelectList}
                    onChange={({ detail }) => {
                      console.log('dtail', detail);
                      setSelectedDocuments([...detail.selectedOptions]);
                    }}
                    tokenLimit={8}
                    expandToViewport
                    enableSelectAll
                    i18nStrings={i18nStrings}
                  />
                </div>
              )}
            </SpaceBetween>
          </>
        </SpaceBetween>
        <Modal visible={showModal} onDismiss={() => setShowModal(false)}>
          <div>
            <h1 className="text-2xl pb-4">No Analyzed Documents</h1>
            <p className="text-sm text-gray-700 mb-4">
              You do not currently have any analyzed documents to search. Please
              create a project and upload documents so that you can search them.
            </p>
          </div>
        </Modal>
        <ScrollDownArrow
          containerRef={filterRef as React.RefObject<HTMLElement>}
          bottomValue="0%"
        />
      </div>
    );
  }
);
